<script setup lang="ts">
import { useRuntimeConfigKey, type UseRuntimeConfig } from '@/injectionKeys';
import { ABOUT_LP_URL } from '@/wabunka-src/constants/aboutLp';
import { ENTERPRISE_LP_URL } from '@/wabunka-src/constants/enterpriseLp';
import { GIFT_LP_URL } from '@/wabunka-src/constants/giftLp';

const useRuntimeConfig = inject(useRuntimeConfigKey) as UseRuntimeConfig;
const runtimeConfig = useRuntimeConfig();
const deploymentUrl = runtimeConfig.public.BASE_URL;
</script>

<template>
  <client-only>
    <div id="jsi-footer">
      <MoleculesScrollTop />
      <footer class="l-footer">
        <div class="footer-info ml-auto mr-auto px-4 py-0">
          <div class="footer-info__row">
            <div class="footer-info__col w-full">
              <NuxtLink to="/"><img alt="Wabunka Logo" class="footer-info__logo" src="@/assets/logo/wabunka/ghost.svg" /></NuxtLink>
            </div>
          </div>
          <div class="footer-info__row main-block">
            <div class="footer-info__row service">
              <div class="footer-info__col service-list">
                <NuxtLink class="footer-info__service-link text-gray1" to="/s/experiences/">
                  <AtomsTextWithArrow class="font-['Spectral'] text-base font-light leading-normal text-zinc-300" :style="{ '--color': '#D9D9D9', '--hover': '#d2a832' }"> Search </AtomsTextWithArrow>
                </NuxtLink>
                <NuxtLink class="footer-info__service-link" to="/contact/">
                  <AtomsTextWithArrow class="font-['Spectral'] text-base font-light leading-normal text-zinc-300 lg:pl-8" :style="{ '--color': '#D9D9D9', '--hover': '#d2a832' }">
                    Contact us
                  </AtomsTextWithArrow>
                </NuxtLink>
              </div>
              <div class="footer-info__col service-list">
                <a class="footer-info__service-link" :href="ABOUT_LP_URL">
                  <AtomsTextWithArrow class="font-['Spectral'] text-base font-light leading-normal text-zinc-300 lg:pl-8" :style="{ '--color': '#D9D9D9', '--hover': '#d2a832' }">
                    About us
                  </AtomsTextWithArrow>
                </a>
                <NuxtLink class="footer-info__service-link" to="/stories/">
                  <AtomsTextWithArrow class="font-['Spectral'] text-base font-light leading-normal text-zinc-300 lg:pl-8" :style="{ '--color': '#D9D9D9', '--hover': '#d2a832' }">
                    Wabunka Stories
                  </AtomsTextWithArrow>
                </NuxtLink>
              </div>
              <div class="footer-info__col service-list">
                <a class="footer-info__service-link" rel="noopener noreferrer" target="_blank" :href="GIFT_LP_URL">
                  <AtomsTextWithArrow class="font-['Spectral'] text-base font-light leading-normal text-zinc-300 lg:pl-8" :style="{ '--color': '#D9D9D9', '--hover': '#d2a832' }">
                    Purchase a gift card
                  </AtomsTextWithArrow>
                </a>
                <NuxtLink class="footer-info__service-link" to="/faq/">
                  <AtomsTextWithArrow class="font-['Spectral'] text-base font-light leading-normal text-zinc-300 lg:pl-8" :style="{ '--color': '#D9D9D9', '--hover': '#d2a832' }">
                    FAQ
                  </AtomsTextWithArrow>
                </NuxtLink>
              </div>
              <div class="footer-info__col service-list">
                <NuxtLink class="footer-info__service-link" to="/lp/partner-with-us/" :prefetch="false">
                  <AtomsTextWithArrow class="font-['Spectral'] text-base font-light leading-normal text-zinc-300 lg:pl-8" :style="{ '--color': '#D9D9D9', '--hover': '#d2a832' }">
                    Partner with us
                  </AtomsTextWithArrow>
                </NuxtLink>
                <a class="footer-info__service-link" rel="noopener noreferrer" target="_blank" :href="ENTERPRISE_LP_URL">
                  <AtomsTextWithArrow class="font-['Spectral'] text-base font-light leading-normal text-zinc-300 lg:pl-8" :style="{ '--color': '#D9D9D9', '--hover': '#d2a832' }">
                    For teams and large groups
                  </AtomsTextWithArrow>
                </a>
              </div>
              <div class="footer-info__col service-list">
                <NuxtLink class="footer-info__service-link" target="_blank" to="https://lp.otonami.jp/recruiting/interpreter" external>
                  <AtomsTextWithArrow class="font-['Spectral'] text-base font-light leading-normal text-zinc-300 lg:pl-8" :style="{ '--color': '#D9D9D9', '--hover': '#d2a832' }">
                    Become a tour guide
                  </AtomsTextWithArrow>
                </NuxtLink>
              </div>
            </div>
          </div>
          <div class="footer-info__col subinfo-area">
            <div class="footer-info__corp">
              <nav class="footer-info__corp-nav">
                <a :href="deploymentUrl + '/pdf/terms.pdf'">Terms of Service</a>
                <a :href="deploymentUrl + '/pdf/privacy-policy.pdf'">Privacy Policy</a>
                <br class="md:hidden" />
                <a href="https://j-cat.co.jp/travel-agency-business" rel="noopener noreferrer" target="_blank">Travel Agency Information & Terms</a>
                <br class="md:hidden" />
                <a :href="deploymentUrl + '/pdf/notice-based-on-the-japanese-act-on-specified-commercial-transactions.pdf'">Specified Commercial Transactions Notice</a>
              </nav>
            </div>
            <div class="footer-info__row">
              <div class="footer-info__col w-full">
                <div class="contact-area flex">
                  <div class="contact-area__sns">
                    <p class="footer-info__social text-gray1">Follow us</p>
                    <div class="footer-info__social-nav">
                      <p class="footer-info__fb-logo">
                        <a href="https://www.facebook.com/wabunka.japan/?ref=page_internal" target="_blank">
                          <img alt="facebook icon" class="w-full" src="@/assets/icon/facebook/ghost.svg" />
                        </a>
                      </p>
                      <p class="footer-info__insta-logo">
                        <a href="https://www.instagram.com/wabunka.japan/" target="_blank">
                          <img alt="instagram icon" class="w-full" src="@/assets/icon/instagram/ghost.svg" />
                        </a>
                      </p>
                      <p class="footer-info__youtube-logo flex items-center">
                        <a href="https://www.youtube.com/@WabunkaJapan" target="_blank">
                          <img alt="youtube icon" class="w-full" src="@/assets/icon/youtube/ghost.svg" />
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p class="footer-info__copyright">
            <span>&copy; 2026 J-CAT, Inc. All rights reserved.</span>
          </p>
        </div>
      </footer>
    </div>
  </client-only>
</template>

<style lang="scss" scoped>
.l-footer {
  border-top: 1px solid #999;
}

a {
  color: #fff;
  text-decoration: none;
}

.pagetop {
  width: 100%;
  height: 40px;
  position: relative;
  background: #222;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  outline: none;
  border-top: 1px solid #999;
  cursor: pointer;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 10px;
    height: 10px;
    border-top: solid 1px #fff;
    border-right: solid 1px #fff;
    transform: rotate(-45deg) translateX(-50%) translateY(-50%);
  }
}

.l-footer {
  padding: 60px 0;
  background-color: #222;
  font-family: 'Noto Sans JP', sans-serif;
  color: #fff;
}

.main-block {
  margin-top: 40px; // 仮

  @media screen and (min-width: 992px) {
    min-width: 927px;
    max-width: 1180px;
    width: 80%;
    margin: 60px auto 0;
  }
}

.service {
  @media screen and (min-width: 992px) {
    align-items: flex-start !important;
    flex-wrap: wrap;
  }
}

.service-list {
  flex-direction: row !important;

  &:first-child {
    border-top: 1px solid #555;

    @media screen and (min-width: 992px) {
      border: none;
    }
  }

  &:last-child {
    border-bottom: 1px solid #555;

    @media screen and (min-width: 992px) {
      border: none;
    }
  }

  .footer-info__service-link {
    width: 50%;
    padding-right: 12px;
    border-right: 1px solid #555;

    @media screen and (min-width: 992px) {
      width: unset;
      padding: 0;
      border: none;
    }
  }

  .footer-info__service-link:nth-child(2) {
    padding-left: 12px;
    border-right: none;
    margin-right: 0;
    padding-right: 0;

    @media screen and (min-width: 992px) {
      padding: 0;
      border: none;
    }
  }

  .footer-info__service-link:nth-child(3) {
    border-bottom: none;
  }
}

.footer-info {
  &__ {
    &row {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &col {
      display: flex;
      flex-direction: column;
    }

    &logo {
      width: 176px;
      height: auto;
      margin: 0 auto;

      @media screen and (min-width: 992px) {
        width: 292px;
      }
    }

    &heading-sub {
      font-size: 10px;
      line-height: 32px;
      letter-spacing: 0.1em;
      text-align: center;
      margin-left: 4px; // 見た目を中心に揃えるため

      @media screen and (min-width: 992px) {
        line-height: 12px;
        letter-spacing: 2px;
      }
    }

    &mail {
      order: -1;
      margin: 24px 0 32px;

      @media screen and (min-width: 992px) {
        order: 0;
        margin: 0;
      }

      p {
        color: #d9d9d9;
        font-family: Noto Serif JP;
        font-size: 14px;
        font-weight: 700;
        line-height: 21px;
        letter-spacing: 2px;

        @media screen and (min-width: 992px) {
          color: #fff;
          font-weight: 400;
        }
      }
    }

    &service-link {
      @media screen and (min-width: 992px) {
        padding: 0;
      }

      a {
        font-family: Noto Serif JP;
        font-size: 12px;
        line-height: 14px;
        letter-spacing: 0px;

        @media screen and (min-width: 992px) {
          color: #fff;
          font-size: 14px;
          line-height: 12px;
          letter-spacing: 0.5px;
        }
      }
    }

    &social {
      margin: 0 auto 24px;
      font-family: 'Spectral';
      font-size: 16px;
      font-weight: 500;
      line-height: 12px;
      letter-spacing: 0px;
      text-align: center;
    }

    &social-nav {
      display: flex;
      justify-content: center;
    }

    &jed-logo {
      width: 60px;
      margin-right: 24px;

      @media screen and (min-width: 992px) {
        width: 80px;
      }
    }

    &fb-logo {
      width: 20px;
      background: url('@/assets/icon/facebook/gold.svg') no-repeat center;
      background-size: contain;
      margin-right: 24px;

      img {
        transition: 0.5s;
      }

      &:hover {
        img {
          opacity: 0;
          transition: 0.5s;
        }
      }
    }

    &insta-logo {
      width: 20px;
      background: url('@/assets/icon/instagram/gold.svg') no-repeat center;
      background-size: contain;
      margin-right: 24px;

      img {
        transition: 0.5s;
      }

      &:hover {
        img {
          opacity: 0;
          transition: 0.5s;
        }
      }
    }

    &youtube-logo {
      width: 20px;
      background: url('@/assets/icon/youtube/gold.svg') no-repeat center;
      background-size: contain;

      img {
        transition: 0.5s;
      }

      &:hover {
        img {
          opacity: 0;
          transition: 0.5s;
        }
      }
    }

    &corp {
      display: block;
      padding: 24px 0 32px;
      font-size: 12px;
      margin: auto;
      text-align: center;

      @media screen and (min-width: 992px) {
        border-bottom: 1px solid #999;
        width: 80%;
        min-width: 927px;
        max-width: 1180px;
      }
    }

    &copyright {
      padding-top: 16px;
      font-size: 1.1px;
      color: #999;
      text-align: center;
    }

    &corp-nav a {
      font-size: 14px;
      font-family: 'Spectral';
      letter-spacing: 0px;
      color: #999;
      transition: all 0.5s;
      padding: 0 8px;

      @media screen and (min-width: 992px) {
        font-size: 16px;
      }

      &:hover {
        color: #d2a83e;
        transition: all 0.5s;
      }
    }

    // &corp-nav a:not(:last-child)::after {
    //   content: '|';
    //   display: inline-block;
    //   padding: 0 8px;
    //   font-size: 12px;
    //   line-height: 29px;
    //   letter-spacing: 0px;
    //   color: #fff;
    //   overflow: initial;
    // }
  }
}

.subinfo-area {
  @media screen and (max-width: 991px) {
    flex-direction: column-reverse;
  }
}

.contact-area {
  flex-direction: column;
  justify-content: center;

  @media screen and (min-width: 992px) {
    flex-direction: row;
  }

  &__ {
    &sns {
      margin: 16px 0 8px;
      padding: 16px 24px;
      min-width: 280px;

      @media screen and (min-width: 992px) {
        margin-right: 12px;
      }
    }

    &tel {
      padding: 20px 40px;
      background-color: #333;
      flex-direction: column;
      justify-content: center;

      @media screen and (min-width: 992px) {
        flex-direction: row;
        margin: 16px 0 8px;
      }
    }
  }
}

.tel-area {
  align-items: center;

  &__ {
    &message {
      font-family: Noto Serif JP;
      font-size: 12px;
      line-height: 22px;
      letter-spacing: 0.5px;
      margin: 0 0 12px;

      @media screen and (min-width: 992px) {
        margin: 0 32px 0 0;
      }
    }

    &sub-message {
      font-family: Noto Serif JP;
      font-size: 10px;
      line-height: 14px;
      letter-spacing: 0px;
      color: #d9d9d9;
    }

    &phone-number {
      font-family: Playfair Display;
      font-size: 26px;
      line-height: 18px;
      letter-spacing: 2px;
      margin-bottom: 20px;

      @media screen and (min-width: 992px) {
        margin: 0 20px 0;
      }
    }

    &line {
      width: 220px;
      height: 40px;
      display: flex;
      justify-content: center;
      align-items: center;
      border: 1px solid #fff;
      margin-bottom: 12px;

      @media screen and (min-width: 992px) {
        margin: 0;
      }
    }

    &line-text {
      font-family: Noto Serif JP;
      font-size: 12px;
      line-height: 22px;
      letter-spacing: 2px;
    }
  }
}

@media screen and (max-width: 991px) {
  .sp-none {
    display: none;
  }

  .l-footer {
    padding: 40px 0;
  }

  .footer-info__row {
    flex-direction: column;
    width: 100%;
  }

  .footer-info__row:nth-of-type(2) .footer-info__col {
    width: 100%;
  }

  .footer-info__service-link {
    width: 50%;
    padding: 5px 0;
    border-bottom: 1px solid #555;
  }

  .footer-info__corp,
  .footer-info__copyright {
    font-size: 10px;
    letter-spacing: 0;
  }

  .footer-info__corp {
    padding: 0;
  }

  .footer-info__copyright {
    padding-top: 24px;
  }
}

@media screen and (min-width: 992px) {
  .footer-info__copyright {
    font-size: 11px;
  }
}

@media screen and (min-width: 992px) {
  .md:hiddenforfooter {
    display: none;
  }
}

// 以下、仮
.kari-middle {
  display: none;

  @media screen and (min-width: 992px) {
    display: flex;
    margin-top: -36px;
  }
}
</style>
